import React, { useContext, useEffect, useRef, useState } from 'react';
import { Breadcrumb, SuccessPopupModal, TextInput } from '../../Components/Atoms';
import { Button, useToast } from '@chakra-ui/react';
import { createDriver, updateDriver, getDrivers } from '../../APIs';
import PATHS from '../../Routes/paths';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StoreContext } from '../../Library/contexts';
import "./style.css";

// Retry limit of 100 guards against degenerate cases where most 4-digit PINs
// are already taken (e.g. account with many drivers). In practice, with 9000
// possible PINs and far fewer drivers, a collision-free PIN is found on the
// first or second attempt.
const generateUniquePin = (usedPins = new Set()) => {
  let attempts = 0;
  while (attempts < 100) {
    const pin = String(Math.floor(1000 + Math.random() * 9000));
    if (!usedPins.has(pin)) return pin;
    attempts++;
  }
  // Fallback: return any random pin if all taken (extremely unlikely)
  return String(Math.floor(1000 + Math.random() * 9000));
};

const AddDriver = () => {
  const navigate = useNavigate();
  const { setDriverManagementCache } = useContext(StoreContext);
  const [searchParams] = useSearchParams();
  const isEdit = searchParams.get("isEdit") === "true";
  const driverIdx = searchParams.get("idx");
  const Toast = useToast();

  const [nm, setNm] = useState("");
  const [pin, setPin] = useState("");
  const [nmError, setNmError] = useState("");
  const [pinError, setPinError] = useState("");
  const [pinJustGenerated, setPinJustGenerated] = useState(false);
  const [pinVisible, setPinVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const usedPinsRef = useRef(new Set());

  const breadcrumbPaths = [
    { idx: 1, name: "Driver Management", link: PATHS.DRIVER_MANAGEMENT },
    { idx: 2, name: isEdit ? "Edit Driver" : "Add Driver", isCurrentPage: true },
  ];

  useEffect(() => {
    // Fetch all drivers to (a) collect used PINs for uniqueness, (b) prefill form in edit mode
    getDrivers((err, data) => {
      if (!err && data) {
        const currentIdx = isEdit ? String(driverIdx) : null;
        const used = new Set(
          data.filter(d => String(d.idx) !== currentIdx).map(d => d.pin)
        );
        usedPinsRef.current = used;

        if (isEdit && driverIdx) {
          const driver = data.find(d => String(d.idx) === String(driverIdx));
          if (driver) {
            setNm(driver.nm || "");
            setPin(driver.pin || "");
          }
        }
      }
    });
  }, [isEdit, driverIdx]);

  const handleGeneratePin = () => {
    const newPin = generateUniquePin(usedPinsRef.current);
    setPin(newPin);
    setPinError("");
    setPinJustGenerated(true);
    setTimeout(() => setPinJustGenerated(false), 600);
  };

  const validate = () => {
    let valid = true;
    if (!nm.trim()) { setNmError("Driver name is required"); valid = false; }
    else setNmError("");
    if (!pin || !/^\d{4}$/.test(pin)) { setPinError("Please generate a PIN"); valid = false; }
    else setPinError("");
    return valid;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setLoading(true);

    const payload = { nm: nm.trim(), pin };

    if (isEdit) {
      updateDriver({ ...payload, idx: driverIdx }, (err) => {
        setLoading(false);
        if (err) Toast({ title: "Error", description: typeof err === "string" ? err : "Failed to update driver", status: "error", duration: 3000, isClosable: true });
        else setModalOpen(true);
      });
    } else {
      createDriver(payload, (err) => {
        setLoading(false);
        if (err) Toast({ title: "Error", description: typeof err === "string" ? err : "Failed to add driver", status: "error", duration: 3000, isClosable: true });
        else setModalOpen(true);
      });
    }
  };

  return (
    <div className="panel-main driver-management-page">
      <div className="add-driver-breadcrumb-row">
        <Breadcrumb paths={breadcrumbPaths} />
      </div>

      <div className="add-driver-form-container">
        <div className="form-header">
          <span className="form-title">{isEdit ? "Edit Driver" : "Add Driver"}</span>
        </div>

        <div className="add-driver-form-content">

          {/* Driver Name */}
          <div className="add-driver-row">
            <label className="add-driver-label">
              Driver Name<span className="required-star">*</span>
            </label>
            <div className="add-driver-input-group">
              <div className="add-driver-input-wrapper">
                <TextInput
                  placeholder="Enter Driver Name"
                  value={nm}
                  onChange={(val) => { setNm(val); if (nmError) setNmError(""); }}
                  errMessage={nmError}
                  isRequired
                  className="add-driver-input"
                  labelClassName="add-driver-text-input-label-wrapper"
                />
              </div>
            </div>
          </div>

          {/* Generate PIN */}
          <div className="add-driver-row">
            <label className="add-driver-label">
              Generate Driver PIN<span className="required-star">*</span>
            </label>
            <div className="add-driver-input-group">
              <div className={`driver-pin-field-wrapper${pinError ? " pin-error" : ""}`}>
                <div className="driver-pin-boxes-row">
                  {[0, 1, 2, 3].map(i => (
                    <div
                      key={i}
                      className={`driver-pin-box${pin[i] ? " filled" : ""}${pinJustGenerated ? " pin-bounce" : ""}`}
                    >
                      {pin[i] ? (pinVisible ? pin[i] : '●') : ""}
                    </div>
                  ))}
                </div>
                <div className="driver-pin-actions-row">
                  {pin && (
                    <button
                      type="button"
                      className="reveal-pin-btn"
                      onClick={() => setPinVisible(v => !v)}
                      title={pinVisible ? "Hide PIN" : "Show PIN"}
                    >
                      {pinVisible ? "Hide" : "Show"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="generate-pin-btn"
                    onClick={handleGeneratePin}
                  >
                    {pin ? "Regenerate PIN" : "Generate PIN"}
                  </button>
                </div>
              </div>
              {pinError && <span className="pin-error-msg">{pinError}</span>}
            </div>
          </div>

          <div className="save-driver-btn-container">
            <Button
              className="save-driver-btn"
              onClick={handleSubmit}
              isLoading={loading}
              loadingText={isEdit ? "Saving…" : "Adding…"}
            >
              {isEdit ? "Save Changes" : "Add Driver"}
            </Button>
          </div>

        </div>
      </div>

      <SuccessPopupModal
        open={modalOpen}
        modalMsg={isEdit ? "Driver updated successfully!" : "Driver added successfully!"}
        closeBtn="Okay"
        onModalClose={() => { setDriverManagementCache({ stats: null, statsRange: null, summary: {}, trips: {} }); navigate(PATHS.DRIVER_MANAGEMENT); }}
      />
    </div>
  );
};

export default AddDriver;
